/**
 * A wrapper component for testing Qerent components in isolation.
 * 
 * This component provides a mock store which can be used to dispatch actions for testing purposes.
 */
import { type FC, type PropsWithChildren, useMemo, useRef } from "react";
import { StubStoreState, initializeStubStore } from "./store";
import { Provider } from "react-redux";
import { AppThemeProvider } from "../src/shared/providers/AppThemeProvider";
import { SnackbarProvider } from "notistack";
import Notifier from "../src/shared/components/Notifier";
import { ClientAppSettings, SettingsProvider } from "../src/shared/providers/SettingsProvider";
import { StaticPlotContext } from "../src/shared/providers/StaticPlotProvider";
import { TooltipContainerContext } from "../src/shared/providers/TooltipContainerProvider";
import { CanvasBindingsContext } from "../src/shared/providers/CanvasBindingsProvider";
import { JsxParserComponentsContext } from "../src/shared/providers/JsxParserComponentsProvider";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { LicenseInfo } from '@mui/x-license';
import * as BaseComponents from "../src/shared/components/CanvasComponents";
import { AuthContext } from "../src/auth/AuthContext";
import { DEFAULT_AUTH_CONTEXT } from "./auth";
import _ from "lodash";

LicenseInfo.setLicenseKey(
    "5531fc5c08fc9a38ae6daa8bc2e124c1Tz0xMjAyODcsRT0xNzkxNzYzMTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI="
);

type RecursivePartial<T> = {
    [P in keyof T]?: RecursivePartial<T[P]>;
};

export type QerentAppStubProps = PropsWithChildren<{
    initialState?: StubStoreState;
    settings?: ClientAppSettings;
    authContext?: RecursivePartial<AuthContext>;
}>;

/**
 * A wrapper component for rendering Qerent components in isolation.
 * 
 * @param initialState - An (optional) initial state for the application store.  Defaults to an empty object.
 * @param children - The components to be rendered within the wrapper.
 * @returns A component which provides all the necessary context/providers for Qerent components to be rendered in isolation.
 */
export const QerentAppStub: FC<QerentAppStubProps> = ({ children, initialState = {}, settings = {} as ClientAppSettings, authContext: _authContext = {} as AuthContext }) => {
    const mockStore = useMemo(() => initializeStubStore(initialState), []);
    const containerRef = useRef<HTMLDivElement>(null);
    const authContext = useMemo(() => _.extend(_.clone(DEFAULT_AUTH_CONTEXT), _authContext), []);

    const bindings = {
        ...(initialState?.canvasState ?? {}),
        ...(initialState?.frameData ?? {}),
        ...(initialState?.parameterValues ?? {}),
    };

    const components = useMemo(() => ({ ...BaseComponents } as Record<string, any>), []);

    return (
        <SettingsProvider settings={settings}>
            <AuthContext.Provider value={authContext}>
                <Provider store={mockStore}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <AppThemeProvider>
                            <SnackbarProvider
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                maxSnack={5}
                            >
                                <Notifier />
                                <div className="grid-1-1" ref={containerRef}>
                                    <JsxParserComponentsContext.Provider value={components}>
                                        <StaticPlotContext.Provider value={false}>
                                            <TooltipContainerContext.Provider value={containerRef}>
                                                <CanvasBindingsContext.Provider value={bindings}>
                                                    {children}
                                                </CanvasBindingsContext.Provider>
                                            </TooltipContainerContext.Provider>
                                        </StaticPlotContext.Provider>
                                    </JsxParserComponentsContext.Provider>
                                </div>
                            </SnackbarProvider>
                        </AppThemeProvider>
                    </LocalizationProvider>
                </Provider>
            </AuthContext.Provider>
        </SettingsProvider>
    );
};